import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { HERO_IMG } from "../../lib/site";
import { scrollTo } from "./Navbar";

const EASE = [0.76, 0, 0.24, 1];

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.35 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1.05, ease: EASE } },
};

const MaskLine = ({ children }) => (
  <span className="block overflow-hidden pb-[0.12em]">
    <motion.span variants={lineChild} className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden grain bg-navy"
    >
      {/* Parallax background */}
      <motion.div style={{ y: yBg }} className="absolute inset-0 -z-0">
        <img src={HERO_IMG} alt="Global cargo shipping" className="h-[120%] w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1F3A] via-[#0B1F3A]/85 to-[#0B1F3A]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#061122] via-transparent to-[#0B1F3A]/60" />
      </motion.div>

      <motion.div style={{ y: yText, opacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full pt-28">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}>
          <span className="inline-flex items-center gap-2 text-xs md:text-sm uppercase tracking-[0.22em] font-semibold text-gold">
            <span className="h-px w-8 bg-gold" /> Global Trading Company
          </span>
        </motion.div>

        <motion.h1
          variants={lineParent}
          initial="hidden"
          animate="show"
          className="mt-7 font-display font-light text-white text-5xl md:text-6xl lg:text-[5.2rem] leading-[1.05] tracking-tighter max-w-5xl"
        >
          <MaskLine>Global Import</MaskLine>
          <MaskLine>& Export <span className="text-gold font-normal">Solutions</span></MaskLine>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
          className="mt-8 text-lg md:text-2xl text-slate-200 font-light max-w-xl"
        >
          Connecting Markets. <span className="text-white font-medium">Delivering Trust.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25, ease: EASE }}
          className="mt-11 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-explore-btn"
            onClick={() => scrollTo("categories")}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-[#0B1F3A] transition-all duration-300 hover:bg-[#DDBE94] hover:scale-105"
          >
            Explore Categories
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-contact-btn"
            onClick={() => scrollTo("contact")}
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-gold/60 px-8 py-4 text-sm font-semibold text-gold transition-all duration-300 hover:bg-gold/10 hover:border-gold"
          >
            Contact Us
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-20 grid grid-cols-3 gap-6 max-w-lg border-t border-white/10 pt-8"
        >
          {[
            { k: "25+", v: "Global Markets" },
            { k: "100%", v: "Quality Assured" },
            { k: "4", v: "Core Categories" },
          ].map((s) => (
            <div key={s.v}>
              <p className="font-display text-3xl md:text-4xl font-semibold text-white">{s.k}</p>
              <p className="mt-1 text-xs md:text-sm text-slate-400">{s.v}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-slate-400">Scroll</span>
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="h-10 w-px bg-gradient-to-b from-gold to-transparent"
        />
      </motion.div>
    </section>
  );
};
